import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, CARD_DESCRIPTOR, siteImages } from '../data/mock';
import { Shield, Bolt } from 'lucide-react';

const PrivacyPage = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | Axon Muscle';
  }, []);

  return (
    <div className="min-h-screen bg-neutral-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Banner */}
        <section className="relative h-56 lg:h-64 overflow-hidden">
          <img 
            src={siteImages.privacyBanner}
            alt="Privacy Policy"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-950 via-neutral-950/90 to-neutral-950/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-transparent to-transparent" />
          
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-axon-600/20 border border-axon-500/30 rounded-full text-axon-400 text-sm font-semibold mb-4">
                <Bolt className="w-4 h-4" />
                LEGAL
              </div>
              <h1 className="font-display text-4xl lg:text-5xl text-white uppercase tracking-tight">
                Privacy Policy
              </h1>
            </div>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-neutral-900 rounded-3xl border border-neutral-800 p-8 lg:p-12">
            {/* Company Info Card */}
            <div className="mb-10 p-6 bg-axon-600/10 border border-axon-500/30 rounded-2xl">
              <div className="flex items-center gap-3 mb-3">
                <Shield className="w-6 h-6 text-axon-500" />
                <span className="text-axon-400 font-semibold">This Privacy Policy is provided by:</span>
              </div>
              <p className="text-white font-semibold text-lg">{CONTACT_INFO.corpName}</p>
              <p className="text-neutral-400">{CONTACT_INFO.address.street}, {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}</p>
            </div>

            <div className="prose prose-invert max-w-none">
              <p className="text-neutral-300 mb-8">
                Last Updated: January 2025
              </p>

              <div className="space-y-8">
                <section>
                  <h2 className="font-display text-xl text-white uppercase tracking-wide mb-4">1. Information We Collect</h2>
                  <p className="text-neutral-400 leading-relaxed">
                    We collect information you provide directly to us, including your name, email address, shipping address, phone number, and payment information when you make a purchase. We also collect information about your browsing activity on our website.
                  </p>
                </section>

                <section>
                  <h2 className="font-display text-xl text-white uppercase tracking-wide mb-4">2. How We Use Your Information</h2>
                  <p className="text-neutral-400 leading-relaxed">
                    We use the information we collect to process your orders, communicate with you about your purchases, send you marketing communications (if you opt in), improve our website and services, and comply with legal obligations.
                  </p>
                </section>

                <section>
                  <h2 className="font-display text-xl text-white uppercase tracking-wide mb-4">3. Information Sharing</h2>
                  <p className="text-neutral-400 leading-relaxed">
                    We do not sell your personal information. We may share your information with service providers who assist us in operating our website and processing orders, or when required by law.
                  </p>
                </section>

                <section>
                  <h2 className="font-display text-xl text-white uppercase tracking-wide mb-4">4. Payment Information</h2>
                  <p className="text-neutral-400 leading-relaxed">
                    All payment transactions are processed securely. Charges will appear on your credit card statement as "<span className="text-axon-400 font-semibold">{CARD_DESCRIPTOR}</span>". We use industry-standard encryption to protect your payment information.
                  </p>
                </section>

                <section>
                  <h2 className="font-display text-xl text-white uppercase tracking-wide mb-4">5. Cookies and Tracking</h2>
                  <p className="text-neutral-400 leading-relaxed">
                    We use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, and understand where our visitors come from. You can control cookie preferences through your browser settings.
                  </p>
                </section>

                <section>
                  <h2 className="font-display text-xl text-white uppercase tracking-wide mb-4">6. Your Rights</h2>
                  <p className="text-neutral-400 leading-relaxed">
                    You have the right to access, correct, or delete your personal information. You may also opt out of marketing communications at any time by clicking the unsubscribe link in our emails or contacting us directly.
                  </p>
                </section>

                <section>
                  <h2 className="font-display text-xl text-white uppercase tracking-wide mb-4">7. Contact Us</h2>
                  <p className="text-neutral-400 leading-relaxed">
                    If you have questions about this Privacy Policy, please contact us at:
                  </p>
                  <div className="mt-4 p-4 bg-neutral-800 rounded-xl">
                    <p className="text-white font-semibold">{CONTACT_INFO.corpName}</p>
                    <p className="text-neutral-400">Email: {CONTACT_INFO.email}</p>
                    <p className="text-neutral-400">Phone: {CONTACT_INFO.phone}</p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PrivacyPage;
